import {useMutation, useQuery} from "@apollo/client";
import pluralize from "pluralize";
import {useEffect, useState} from "react";
import {toast} from "react-hot-toast";

import Button from "@/components/button/Button.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {
  CheckResetListResponse,
  GQL_CHECK_RESET_LIST_QUERY,
  GQL_PLAN_INFO,
  GQL_RESET_LOCATIONS,
  ResetLocationsResponse,
} from "@/lib/queries/billing.ts";
import {Plan} from "@/lib/types/billing.ts";

export interface ResetLocationsActionProps {
  plan: Plan;
}

export const ResetLocationsAction = ({plan}: ResetLocationsActionProps) => {
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const {data, loading} = useQuery<CheckResetListResponse>(GQL_CHECK_RESET_LIST_QUERY, {
    variables: {plan: plan},
  });
  const [resetLocations] = useMutation<ResetLocationsResponse>(GQL_RESET_LOCATIONS, {
    variables: {plan: plan},
    refetchQueries: [
      {
        query: GQL_PLAN_INFO,
        variables: {
          plan,
          ignoreAccountPlan: true,
        },
      },
    ],
  });
  useEffect(() => {
    setErrors([]);
  }, []);
  return (
    <>
      <Button variant={"secondary"} onClick={() => setShow(true)}>
        Reset Locations to Plan
      </Button>
      <ActionDialog
        show={show}
        title="Confirm Reset Locations"
        confirmBtnText="Reset Locations"
        loading={loading}
        onCancel={() => setShow(false)}
        onConfirm={() => {
          setErrors([]);
          resetLocations({variables: {plan: plan}}).then((d) => {
            if (d.data?.resetLocations.success) {
              setShow(false);
              toast.success("Locations has been reset.");
            } else {
              setErrors(
                d.data?.resetLocations.errors["monitoring_groups"] || ["An error occurred. Please contact our support."]
              );
            }
          });
        }}
      >
        <FormErrorAlerts errors={errors} />
        <p>
          Are you sure you want to reset locations on existing checks to the default plan’s locations? No other changes
          to checks will be made.
        </p>
        <strong>
          Here are top {data?.checkResetList.total} {pluralize("check", data?.checkResetList.total)} that will be
          affected:
        </strong>
        <ul>
          {data?.checkResetList.checks.map((check) => (
            <li key={check.id}>
              <a href={check.url} target="_blank">
                {check.name}
              </a>
            </li>
          ))}
        </ul>
      </ActionDialog>
    </>
  );
};
